import React from 'react'
import { ButtonConfig, ProductOrientation, SkinProductOption } from '../types/productTypes'
import './ConfigurableProductView.scss'
import classNames from 'classnames'
import {
  PlaceholderSkin,
  PlaceholderSkinOther,
  PlaceholderSkinSocket,
  PlaceholderSkinSocketVertical,
} from '../data/placeholderImages'
import FillImageView from './FillImageView'
import { getProductImageUrl } from '../products/getProductImageUrl'
import { getSkinSlotPositions } from '../products/productUtils'
import { getProductImageRenderSpec, ImageRotation } from '../utils/ProductRenderSpec'
import { ButtonPercentSizeOfSkin } from './ProductImageConstants'

interface ButtonProps {
  x: number
  y: number
  scale: number
}

interface ButtonFrameProps extends ButtonProps {
  children?: React.ReactNode
  highlight?: boolean
  size?: string
  clickable?: boolean
  zIndex?: number
  preventTooltips?: boolean
}

export const ConfigurableProductButtonFrame: React.FC<ButtonFrameProps> = ({ x, y, scale, children, highlight, size: absoluteSize, clickable, zIndex }) => {
  const size = absoluteSize ?? `${ButtonPercentSizeOfSkin * scale}%`

  return (
    <div
      className={classNames('ConfigurableProductButtonFrame positioned tooltipContainer', { highlight, clickable })}
      style={{
        width: size,
        height: size,
        left: `${x}%`,
        top: `${y}%`,
        zIndex: zIndex,
      }}
    >
      {children}
    </div>
  )
}

export const InlineConfigurableProductButtonFrame: React.FC<{ children: React.ReactChild, highlight?: boolean, size: string, clickable?: boolean }> = ({ children, highlight, size, clickable }) => {
  return (
    <div
      className={classNames('ConfigurableProductButtonFrame inline tooltipContainer', { highlight, clickable })}
      style={{
        width: size,
        height: size,
      }}
    >
      {children}
    </div>
  )
}

type ButtonRenderFunction = (config: ButtonConfig, index: number, scale: number, shadowScale: number | undefined) => React.ReactChild

interface ConfigurableProductViewProps extends SkinProductOption {
  renderButton: ButtonRenderFunction
  className?: string
  buttonCoordsOrientation: ProductOrientation
  placeholderImage: string
  errorImage: string
  shadowScale?: number
  debugDescription?: string
  debugMode?: boolean
}

const ConfigurableProductView: React.FC<ConfigurableProductViewProps> = (props) => {
  const {
    image,
    placeholderImage,
    errorImage,
    color: skinColor,
    renderButton,
    className,
    orientation: productOrientation,
    imageOrientation,
    buttonCoordsOrientation,
    shadowScale: overrideShadowScale,
    debugDescription,
    debugMode,
    imageScale: imageScaleOverride,
  } = props

  const renderSpec = getProductImageRenderSpec(
    image,
    imageScaleOverride,
    productOrientation,
    imageOrientation,
    buttonCoordsOrientation,
    getSkinSlotPositions(props),
  )

  const { skinImageRotation, imageScale, slotPositions } = renderSpec

  const shadowScale = (overrideShadowScale ?? 1) * imageScale
  const shadowStyle: React.CSSProperties = {
    // compensate for image rotation
    filter: skinImageRotation === ImageRotation.QuarterTurnAnticlockwise
      ? `drop-shadow(${-10 * shadowScale}px 0 ${5 * shadowScale}px #0003) drop-shadow(0 0 ${2 * shadowScale}px #0007)`
      : `drop-shadow(0 ${10 * shadowScale}px ${5 * shadowScale}px #0003) drop-shadow(0 0 ${2 * shadowScale}px #0007)`
  }

  const skinColorClassname = skinColor === null ? '' : skinColor

  const skinImage = getProductImageUrl(image || placeholderImage)

  const isImageRotated = skinImageRotation !== ImageRotation.None

  return (
    <div
      className={classNames('ConfigurableProductView', className, { rotated: isImageRotated, horizontal: !isImageRotated && productOrientation === 'Horizontal' }, { [skinColorClassname]: !image })}
    >
      <div
        className="ConfigurableProductView-faceplate"
        style={{
          pointerEvents:'none'
          // ...(
          //   !image ? {
          //     [imageOrientation === 'Vertical' ? 'width' : 'height']: '65%',
          //     [imageOrientation === 'Vertical' ? 'left' : 'top']: '17.5%',
          //     // backgroundPosition: 'center',
          //     // backgroundSize: 'cover',
          //   } : { }
          // ),
          // backgroundImage: `url(${image || placeholderImage})`,
        }}
      >
        <FillImageView
          loadingIndicator
          src={skinImage}
          placeholder={errorImage}
          style={shadowStyle}
        />
      </div>
      {
        slotPositions.map((coords, index) => (
          <React.Fragment key={index}>
            {renderButton(coords, index, imageScale, shadowScale * imageScale)}
          </React.Fragment>
        )
          // (buttonFactory ?? DefaultButtonFactory)(coords, index, scale)
        )
      }
      {
        debugDescription && (debugMode || !image || [PlaceholderSkin, PlaceholderSkinOther, PlaceholderSkinSocket, PlaceholderSkinSocketVertical].includes(image)) && (
          <div
            onClick={e => {
              e.stopPropagation()
            }}
            style={{
              position: 'absolute',
              left: '10%',
              right: '10%',
              bottom: '5%',
              color: '#000',
              fontWeight: 700,
              filter: 'drop-shadow(0 0 3px #fff) drop-shadow(0 0 3px #fff) drop-shadow(0 0 3px #fff)',
              textAlign: 'center',
              fontFamily: 'monospace',
              fontSize: '12px',
              whiteSpace: 'pre',
            }}
          >
            {debugDescription}
          </div>
        )
      }
    </div>
  )
}

export default ConfigurableProductView
